import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

import i18n from "i18next";

import { getCurrentAdminUserData, verifyAdminUser } from "services";

import {
  checkAdminAccess,
  getLanguageFromLocalStorage,
  getLocalKlimVar,
  scrollPage,
  setLocalKlimVar,
  useCancelAsyncOnUnmount,
  useLocation,
  useNavigate,
} from "utils";

import { LANGUAGE_CODE } from "consts";

import { AdminUserDetails } from "models";

type AdminUserContextType = {
  user: AdminUserDetails | null;
  setUser: (user: AdminUserDetails | null) => void;
};

// create user context provider
const AdminUserContext = createContext<AdminUserContextType>({
  user: null,
  setUser: () => null,
});

export const AdminUserContextProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const signal = useCancelAsyncOnUnmount()();

  const [user, setUser] = useState<AdminUserDetails | null>(null);

  const ignorePathsForSignin = ["/sign-in", "/password", "/sign-out", "/confirm-sign-in", "admin-request", "admin-confirm"]; // eslint-disable-line

  const clearUser = useCallback(() => {
    setUser(null);
    setLocalKlimVar("token", null);
    if (!ignorePathsForSignin.some((p) => location.pathname.includes(p))) {
      navigate("/sign-in");
    }
  }, [navigate, location.pathname, ignorePathsForSignin]);

  const verifyUserSession = useCallback(() => {
    if (getLocalKlimVar("token") && !ignorePathsForSignin.some((p) => location.pathname.includes(p))) {
      verifyAdminUser({
        signal,
      })
        .then((res) => {
          if (!user && res.verified) {
            getCurrentAdminUserData({
              signal,
            }).then((res) => {
              setUser(res);
            });
          } else if (!res.verified) {
            clearUser();
          }
        })
        .catch((_err) => {
          clearUser();
        });
    } else {
      clearUser();
    }
  }, [user, signal, clearUser, ignorePathsForSignin, location.pathname]);

  const verifyUserAccess = useCallback(() => {
    if (user && getLocalKlimVar("token") && !checkAdminAccess(user.role, location.pathname.replace(/^\/|\/$/g, ""), true)) {
      navigate("/dashboard");
    }
  }, [user, navigate, location]);

  useEffect(() => {
    verifyUserAccess();
  }, [location.pathname, user]); // eslint-disable-line

  useEffect(() => {
    scrollPage();
    verifyUserSession();
  }, [location.pathname]); // eslint-disable-line

  useEffect(() => {
    i18n.changeLanguage(getLanguageFromLocalStorage() ?? LANGUAGE_CODE.ENGLISH);
  }, []);

  return <AdminUserContext.Provider value={{ user, setUser }}>{children}</AdminUserContext.Provider>;
};

export const useAdminUserContext = () => useContext(AdminUserContext);
